import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db, storage } from '../firebase/config';
import { 
    collection, 
    query, 
    orderBy, 
    onSnapshot, 
    addDoc, 
    deleteDoc, 
    doc,
    serverTimestamp,
    updateDoc,
    arrayUnion,
    arrayRemove,
    increment,
    limit,
    startAfter,
    getDocs
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Swal from 'sweetalert2';
import './Gallery.css';

const Gallery = () => {
    const { currentUser, userData, isAdmin } = useAuth();
    const { showToast } = useToast();
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [selectedAlbum, setSelectedAlbum] = useState(null);
    const [showAlbumModal, setShowAlbumModal] = useState(false);
    const [selectedAlbums, setSelectedAlbums] = useState(new Set());
    const [formData, setFormData] = useState({
        title: '',
        date: '',
        files: [],
        imageUrls: [],
        googlePhotosLink: '',
        googlePhotosEmbedUrl: '',
        thumbnailSource: 'file' // 'file', 'url', or 'google'
    });
    const [previews, setPreviews] = useState([]);
    const [urlPreviews, setUrlPreviews] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);
    const [selectedUrlThumbnailIndex, setSelectedUrlThumbnailIndex] = useState(0);
    const [urlInput, setUrlInput] = useState('');
    const [googlePhotosLink, setGooglePhotosLink] = useState('');
    const [googlePhotosPreview, setGooglePhotosPreview] = useState('');
    const [albumStats, setAlbumStats] = useState({
        totalAlbums: 0,
        totalPhotos: 0,
        recentAlbums: 0
    });
    const [activeMediaTab, setActiveMediaTab] = useState('upload');
    
    // Pagination states
    const [displayedAlbums, setDisplayedAlbums] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [albumsPerPage] = useState(12);
    const observerRef = useRef(null);
    const lastAlbumRef = useRef(null);

    const CLOUDINARY_CLOUD_NAME = 'dblffn98e';
    const CLOUDINARY_UPLOAD_PRESET = 'WHITE DOVES MINISTRY';

    useEffect(() => {
        loadAlbums();
    }, []);

    // Calculate album statistics
    useEffect(() => {
        if (albums.length > 0) {
            const totalPhotos = albums.reduce((sum, album) => sum + (album.photoCount || 0), 0);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            const recentAlbums = albums.filter(album => 
                new Date(album.createdAt?.toDate?.() || album.createdAt) > thirtyDaysAgo
            ).length;

            setAlbumStats({
                totalAlbums: albums.length,
                totalPhotos,
                recentAlbums
            });
        }
    }, [albums]);

    // Update displayed albums when albums change
    useEffect(() => {
        setDisplayedAlbums(albums.slice(0, albumsPerPage));
        setHasMore(albums.length > albumsPerPage);
        setPage(1);
    }, [albums, albumsPerPage]);

    // Setup intersection observer for infinite scroll
    useEffect(() => {
        if (loading) return;

        const options = {
            root: null,
            rootMargin: '100px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries) => {
            const firstEntry = entries[0];
            if (firstEntry.isIntersecting && hasMore && !loadingMore) {
                loadMoreAlbums();
            }
        }, options);

        const currentElement = lastAlbumRef.current;
        if (currentElement) {
            observer.observe(currentElement);
        }

        return () => {
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    }, [hasMore, loadingMore, loading, displayedAlbums]);

    const loadMoreAlbums = () => {
        if (loadingMore || !hasMore) return;
        
        setLoadingMore(true);
        const nextPage = page + 1;
        const startIndex = 0;
        const endIndex = nextPage * albumsPerPage;
        
        setTimeout(() => {
            const newAlbums = albums.slice(startIndex, endIndex);
            setDisplayedAlbums(newAlbums);
            setPage(nextPage);
            setHasMore(endIndex < albums.length);
            setLoadingMore(false);
        }, 500);
    };

    const loadAlbums = () => {
        const albumsQuery = query(
            collection(db, 'gallery-albums'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(albumsQuery, (snapshot) => {
            const albumsData = [];
            snapshot.forEach(doc => {
                albumsData.push({ id: doc.id, ...doc.data() });
            });
            setAlbums(albumsData);
            setLoading(false);
        }, (error) => {
            console.error('Error loading albums:', error);
            showToast('Error loading gallery', 'error');
            setLoading(false);
        });

        return unsubscribe;
    };

    // Validate Google Photos link
    const validateGooglePhotosLink = (url) => {
        const patterns = [
            /https:\/\/photos\.app\.goo\.gl\/[a-zA-Z0-9]+/,
            /https:\/\/photos\.google\.com\/share\/[a-zA-Z0-9_-]+/,
            /https:\/\/photos\.google\.com\/album\/[a-zA-Z0-9_-]+/,
            /https:\/\/photos\.app\.goo\.gl\/[a-zA-Z0-9_-]+/
        ];
        return patterns.some(pattern => pattern.test(url));
    };

    // Generate Google Photos embed URL
    const getGooglePhotosEmbedUrl = (url) => {
        return url;
    };

    // Handle Google Photos link
    const handleAddGooglePhotosLink = async () => {
        if (!googlePhotosLink.trim()) {
            showToast('Please enter a Google Photos link', 'error');
            return;
        }

        if (!validateGooglePhotosLink(googlePhotosLink)) {
            showToast('Invalid Google Photos link. Please use a valid Google Photos share link.', 'error');
            return;
        }

        if (formData.googlePhotosLink === googlePhotosLink) {
            showToast('This link is already added', 'error');
            return;
        }

        const embedUrl = getGooglePhotosEmbedUrl(googlePhotosLink);
        
        setFormData(prev => ({
            ...prev,
            googlePhotosLink: googlePhotosLink,
            googlePhotosEmbedUrl: embedUrl,
            thumbnailSource: 'google'
        }));

        setGooglePhotosPreview('https://www.google.com/images/icons/product/album-32.png');
        
        setGooglePhotosLink('');
        showToast('Google Photos link added successfully!', 'success');
    };

    // Handle removing Google Photos link
    const removeGooglePhotosLink = () => {
        setFormData(prev => ({
            ...prev,
            googlePhotosLink: '',
            googlePhotosEmbedUrl: '',
            thumbnailSource: 'file'
        }));
        setGooglePhotosPreview('');
    };

    // Validate image URL
    const validateImageUrl = (url) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.crossOrigin = 'anonymous';
            img.src = url;
        });
    };

    // Handle adding URL
    const handleAddUrl = async () => {
        if (!urlInput.trim()) {
            showToast('Please enter an image URL', 'error');
            return;
        }

        try {
            new URL(urlInput);
        } catch {
            showToast('Invalid URL format', 'error');
            return;
        }

        if (formData.imageUrls.includes(urlInput)) {
            showToast('This URL is already added', 'error');
            return;
        }

        const isValid = await validateImageUrl(urlInput);
        if (!isValid) {
            showToast('Invalid image URL. Please make sure it\'s a valid image.', 'error');
            return;
        }

        setFormData(prev => ({
            ...prev,
            imageUrls: [...prev.imageUrls, urlInput]
        }));

        setUrlPreviews(prev => [...prev, urlInput]);
        setUrlInput('');
        showToast('Image URL added successfully!', 'success');
    };

    // Handle removing URL
    const removeUrl = (index) => {
        setFormData(prev => ({
            ...prev,
            imageUrls: prev.imageUrls.filter((_, i) => i !== index)
        }));
        setUrlPreviews(prev => prev.filter((_, i) => i !== index));
        if (selectedUrlThumbnailIndex === index) {
            setSelectedUrlThumbnailIndex(0);
        } else if (selectedUrlThumbnailIndex > index) {
            setSelectedUrlThumbnailIndex(prev => prev - 1);
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = files.filter(file => file.type.startsWith('image/'));
        
        if (validFiles.length === 0) {
            showToast('Please select valid image files', 'error');
            return;
        }

        const totalSize = validFiles.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > 50 * 1024 * 1024) {
            showToast('Total file size must be less than 50MB', 'error');
            return;
        }

        setFormData(prev => ({ ...prev, files: [...prev.files, ...validFiles] }));

        const newPreviews = [];
        validFiles.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                newPreviews.push(reader.result);
                if (newPreviews.length === validFiles.length) {
                    setPreviews(prev => [...prev, ...newPreviews]);
                }
            };
            reader.readAsDataURL(file);
        });
    };

    const removeFile = (index) => {
        setFormData(prev => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index)
        }));
        setPreviews(prev => prev.filter((_, i) => i !== index));
        if (selectedThumbnailIndex === index) {
            setSelectedThumbnailIndex(0);
        } else if (selectedThumbnailIndex > index) {
            setSelectedThumbnailIndex(prev => prev - 1);
        }
    };

    const uploadToCloudinary = async (file, progressCallback) => {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    const percent = Math.round((e.loaded / e.total) * 100);
                    progressCallback(percent);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    const data = JSON.parse(xhr.responseText);
                    resolve({
                        url: data.secure_url,
                        publicId: data.public_id,
                        width: data.width,
                        height: data.height,
                        format: data.format
                    });
                } else {
                    reject(new Error('Upload failed'));
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Network error'));
            });

            xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`);
            xhr.send(formData);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            showToast('Please enter an album title', 'error');
            return;
        }

        if (!formData.date) {
            showToast('Please select a date', 'error');
            return;
        }

        const hasMedia = formData.files.length > 0 || 
                        formData.imageUrls.length > 0 || 
                        formData.googlePhotosLink;

        if (!hasMedia) {
            showToast('Please upload photos, add image URLs, or add a Google Photos link', 'error');
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            const photoUrls = [];
            const photoDetails = [];
            let totalItems = formData.files.length + formData.imageUrls.length + (formData.googlePhotosLink ? 1 : 0);
            let processedCount = 0;

            // Process uploaded files
            for (let i = 0; i < formData.files.length; i++) {
                const file = formData.files[i];
                const progress = ((i + 1) / totalItems) * 100;
                setUploadProgress(Math.round(progress));
                
                const result = await uploadToCloudinary(file, (fileProgress) => {
                    const overallProgress = ((i + 0.5) / totalItems) * 100 + (fileProgress / 100) * (100 / totalItems);
                    setUploadProgress(Math.round(overallProgress));
                });
                
                photoUrls.push(result.url);
                photoDetails.push({
                    url: result.url,
                    publicId: result.publicId,
                    width: result.width,
                    height: result.height,
                    format: result.format,
                    source: 'upload'
                });
                processedCount++;
                setUploadProgress(Math.round((processedCount / totalItems) * 100));
            }

            // Process URL images
            for (let i = 0; i < formData.imageUrls.length; i++) {
                const url = formData.imageUrls[i];
                const progress = ((formData.files.length + i + 1) / totalItems) * 100;
                setUploadProgress(Math.round(progress));
                
                photoUrls.push(url);
                photoDetails.push({
                    url: url,
                    source: 'url'
                });
                processedCount++;
                setUploadProgress(Math.round((processedCount / totalItems) * 100));
            }

            // Process Google Photos link
            if (formData.googlePhotosLink) {
                const progress = ((formData.files.length + formData.imageUrls.length + 1) / totalItems) * 100;
                setUploadProgress(Math.round(progress));
                
                photoUrls.push(formData.googlePhotosLink);
                photoDetails.push({
                    url: formData.googlePhotosLink,
                    embedUrl: formData.googlePhotosEmbedUrl,
                    source: 'google'
                });
                processedCount++;
                setUploadProgress(Math.round((processedCount / totalItems) * 100));
            }

            // Determine thumbnail
            let thumbnailUrl = '';
            let thumbnailDetails = {};

            if (formData.thumbnailSource === 'google' && formData.googlePhotosLink) {
                thumbnailUrl = 'https://www.google.com/images/icons/product/album-64.png';
                thumbnailDetails = {
                    url: 'https://www.google.com/images/icons/product/album-64.png',
                    source: 'google',
                    isThumbnail: true
                };
            } else if (formData.thumbnailSource === 'url' && formData.imageUrls.length > 0) {
                const urlIndex = selectedUrlThumbnailIndex;
                thumbnailUrl = formData.imageUrls[urlIndex];
                thumbnailDetails = {
                    url: formData.imageUrls[urlIndex],
                    source: 'url',
                    isThumbnail: true
                };
            } else if (formData.files.length > 0) {
                const thumbnailFile = formData.files[selectedThumbnailIndex];
                const result = await uploadToCloudinary(thumbnailFile, () => {});
                thumbnailUrl = result.url;
                thumbnailDetails = {
                    url: result.url,
                    publicId: result.publicId,
                    source: 'upload',
                    isThumbnail: true
                };
            } else if (formData.imageUrls.length > 0) {
                thumbnailUrl = formData.imageUrls[0];
                thumbnailDetails = {
                    url: formData.imageUrls[0],
                    source: 'url',
                    isThumbnail: true
                };
            } else if (formData.googlePhotosLink) {
                thumbnailUrl = 'https://www.google.com/images/icons/product/album-64.png';
                thumbnailDetails = {
                    url: 'https://www.google.com/images/icons/product/album-64.png',
                    source: 'google',
                    isThumbnail: true
                };
            }

            const albumData = {
                title: formData.title,
                date: formData.date,
                photos: photoUrls,
                photoDetails: photoDetails,
                thumbnailUrl: thumbnailUrl,
                thumbnailDetails: thumbnailDetails,
                thumbnailSource: formData.thumbnailSource || 'file',
                photoCount: photoUrls.length,
                googlePhotosLink: formData.googlePhotosLink || null,
                googlePhotosEmbedUrl: formData.googlePhotosEmbedUrl || null,
                createdAt: serverTimestamp(),
                createdBy: currentUser.uid,
                createdByName: userData?.fullName || userData?.firstName || 'Unknown',
                viewCount: 0,
                downloadCount: 0,
                featured: false,
                tags: generateTags(formData.title)
            };

            await addDoc(collection(db, 'gallery-albums'), albumData);

            showToast(`Album created successfully with ${photoUrls.length} photos!`, 'success');
            resetForm();
            setShowForm(false);

            // Send notification emails
            try {
                await fetch('/.netlify/functions/send-email', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        to: 'all-users',
                        subject: '📸 New Gallery Album Added!',
                        template: 'newGallery',
                        data: {
                            title: formData.title,
                            photoCount: photoUrls.length,
                            date: formData.date,
                            createdBy: userData?.fullName || 'A ministry member'
                        }
                    })
                });
            } catch (emailError) {
                console.log('Email notification skipped:', emailError);
            }

        } catch (error) {
            console.error('Error creating album:', error);
            showToast('Error creating album: ' + error.message, 'error');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const generateTags = (title) => {
        const words = title.toLowerCase().split(' ');
        return words.filter(word => word.length > 2);
    };

    const resetForm = () => {
        setFormData({
            title: '',
            date: '',
            files: [],
            imageUrls: [],
            googlePhotosLink: '',
            googlePhotosEmbedUrl: '',
            thumbnailSource: 'file'
        });
        setPreviews([]);
        setUrlPreviews([]);
        setGooglePhotosPreview('');
        setUrlInput('');
        setGooglePhotosLink('');
        setSelectedThumbnailIndex(0);
        setSelectedUrlThumbnailIndex(0);
        setActiveMediaTab('upload');
    };

    const handleDeleteAlbum = async (albumId) => {
        if (!isAdmin) {
            showToast('Only administrators can delete albums', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Delete Album?',
            text: 'This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const album = albums.find(a => a.id === albumId);
                
                if (album?.photoDetails) {
                    console.log('Photos would be deleted from Cloudinary');
                }

                await deleteDoc(doc(db, 'gallery-albums', albumId));
                showToast('Album deleted successfully!', 'success');
                
                if (selectedAlbums.has(albumId)) {
                    const newSelected = new Set(selectedAlbums);
                    newSelected.delete(albumId);
                    setSelectedAlbums(newSelected);
                }
            } catch (error) {
                console.error('Error deleting album:', error);
                showToast('Error deleting album', 'error');
            }
        }
    };

    const handleSelectAlbum = (albumId) => {
        const newSelected = new Set(selectedAlbums);
        if (newSelected.has(albumId)) {
            newSelected.delete(albumId);
        } else {
            newSelected.add(albumId);
        }
        setSelectedAlbums(newSelected);
    };

    const handleClearSelection = () => {
        setSelectedAlbums(new Set());
    };

    const handleDownloadAlbum = async (album) => {
        try {
            showToast(`Preparing ${album.photoCount} photos for download...`, 'info');
            
            const albumRef = doc(db, 'gallery-albums', album.id);
            await updateDoc(albumRef, {
                downloadCount: increment(1)
            });

            if (album.googlePhotosLink) {
                window.open(album.googlePhotosLink, '_blank');
                showToast('Opening Google Photos album...', 'success');
                return;
            }

            const downloadablePhotos = album.photos.filter(url => {
                return url.includes('cloudinary') || url.match(/\.(jpg|jpeg|png|gif|webp)/i);
            });

            for (let i = 0; i < downloadablePhotos.length; i++) {
                const photoUrl = downloadablePhotos[i];
                const filename = `${album.title.replace(/[^a-z0-9]/gi, '_')}_${i + 1}.jpg`;
                
                const link = document.createElement('a');
                link.href = photoUrl;
                link.download = filename;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                if (i < downloadablePhotos.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            }
            
            showToast('Download complete!', 'success');
        } catch (error) {
            console.error('Error downloading album:', error);
            showToast('Error downloading album', 'error');
        }
    };

    const handleDownloadSelected = async () => {
        const selectedAlbumsList = albums.filter(album => selectedAlbums.has(album.id));
        
        if (selectedAlbumsList.length === 0) return;

        showToast(`Downloading ${selectedAlbumsList.length} albums...`, 'info');

        for (const album of selectedAlbumsList) {
            await handleDownloadAlbum(album);
        }

        setSelectedAlbums(new Set());
    };

    const handleViewAlbum = (album) => {
        const albumRef = doc(db, 'gallery-albums', album.id);
        updateDoc(albumRef, {
            viewCount: increment(1)
        }).catch(err => console.log('Error tracking view:', err));

        if (album.googlePhotosLink) {
            window.open(album.googlePhotosLink, '_blank');
            return;
        }

        setSelectedAlbum(album);
        setShowAlbumModal(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return 'Invalid date';
        }
    };

    const formatRelativeTime = (timestamp) => {
        if (!timestamp) return '';
        
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) return 'just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
        return formatDate(date);
    };

    if (loading) {
        return (
            <div className="gallery-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading gallery...</p>
            </div>
        );
    }

    return (
        <div className="gallery-page">
            <div className="gallery-header">
                <div>
                    <h1 className="gallery-title">Moments Gallery</h1>
                    <p className="gallery-subtitle">Capturing God's work through our ministry</p>
                </div>
                {isAdmin && (
                    <button
                        className="gallery-add-btn"
                        onClick={() => setShowForm(!showForm)}
                    >
                        <i className={`fas fa-${showForm ? 'times' : 'plus'}`}></i>
                        {showForm ? 'Cancel' : 'New Album'}
                    </button>
                )}
            </div>

            {/* Gallery Stats */}
            {albums.length > 0 && (
                <div className="gallery-stats">
                    <div className="gallery-stat-card">
                        <i className="fas fa-images"></i>
                        <div className="gallery-stat-info">
                            <span className="gallery-stat-value">{albumStats.totalAlbums}</span>
                            <span className="gallery-stat-label">Total Albums</span>
                        </div>
                    </div>
                    <div className="gallery-stat-card">
                        <i className="fas fa-camera"></i>
                        <div className="gallery-stat-info">
                            <span className="gallery-stat-value">{albumStats.totalPhotos}</span>
                            <span className="gallery-stat-label">Total Photos</span>
                        </div>
                    </div>
                    <div className="gallery-stat-card">
                        <i className="fas fa-clock"></i>
                        <div className="gallery-stat-info">
                            <span className="gallery-stat-value">{albumStats.recentAlbums}</span>
                            <span className="gallery-stat-label">Last 30 Days</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Form - Keep as is */}
            {showForm && isAdmin && (
                <div className="gallery-form-container">
                    {/* Form content remains the same */}
                    <h2 className="gallery-form-title">Create New Album</h2>
                    <form onSubmit={handleSubmit} className="gallery-form">
                        {/* ... form fields ... */}
                    </form>
                </div>
            )}

            {/* Gallery Grid with Lazy Loading */}
            <div className="gallery-grid">
                {displayedAlbums.length === 0 ? (
                    <div className="gallery-empty">
                        <i className="fas fa-images"></i>
                        <h3>No Albums Yet</h3>
                        <p>Create your first photo album to get started</p>
                    </div>
                ) : (
                    displayedAlbums.map((album, index) => {
                        const isLast = index === displayedAlbums.length - 1;
                        return (
                            <div 
                                key={album.id} 
                                className="gallery-album-card"
                                ref={isLast ? lastAlbumRef : null}
                            >
                                <div className="gallery-album-thumbnail">
                                    <LazyLoadImage
                                        src={album.thumbnailUrl}
                                        alt={album.title}
                                        effect="blur"
                                        placeholderSrc="https://via.placeholder.com/300x200?text=Loading..."
                                        onClick={() => handleViewAlbum(album)}
                                        className="gallery-lazy-image"
                                        width="100%"
                                        height="200px"
                                        style={{ objectFit: 'cover' }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://www.google.com/images/icons/product/album-64.png';
                                        }}
                                    />
                                    <span className="gallery-photo-count">
                                        <i className="fas fa-images"></i> {album.photoCount}
                                    </span>
                                    {album.googlePhotosLink && (
                                        <span className="gallery-google-badge">
                                            <i className="fab fa-google"></i>
                                        </span>
                                    )}
                                    {isAdmin && (
                                        <button
                                            className={`gallery-select-btn ${selectedAlbums.has(album.id) ? 'selected' : ''}`}
                                            onClick={() => handleSelectAlbum(album.id)}
                                        >
                                            <i className={`fas ${selectedAlbums.has(album.id) ? 'fa-check-square' : 'fa-square'}`}></i>
                                        </button>
                                    )}
                                    <span className="gallery-view-count">
                                        <i className="fas fa-eye"></i> {album.viewCount || 0}
                                    </span>
                                </div>
                                <div className="gallery-album-info">
                                    <h3 className="gallery-album-title">{album.title}</h3>
                                    <p className="gallery-album-date">
                                        <i className="far fa-calendar-alt"></i>
                                        {formatDate(album.date)}
                                    </p>
                                    <p className="gallery-album-uploaded">
                                        <i className="far fa-clock"></i>
                                        {formatRelativeTime(album.createdAt)}
                                    </p>
                                    <div className="gallery-album-actions">
                                        <button
                                            className="gallery-view-btn"
                                            onClick={() => handleViewAlbum(album)}
                                        >
                                            <i className="fas fa-eye"></i> View
                                        </button>
                                        <button
                                            className="gallery-download-btn"
                                            onClick={() => handleDownloadAlbum(album)}
                                        >
                                            <i className="fas fa-download"></i>
                                        </button>
                                        {isAdmin && (
                                            <button
                                                className="gallery-delete-btn"
                                                onClick={() => handleDeleteAlbum(album.id)}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Loading More Indicator */}
            {loadingMore && (
                <div className="gallery-loading-more">
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Loading more albums...</span>
                </div>
            )}

            {/* End of Results */}
            {!hasMore && albums.length > 0 && (
                <div className="gallery-end-of-results">
                    <p>You've reached the end of the gallery</p>
                </div>
            )}

            {/* Bulk Actions Bar */}
            {selectedAlbums.size > 0 && (
                <div className="gallery-bulk-actions">
                    <span className="gallery-selected-count">
                        {selectedAlbums.size} album{selectedAlbums.size !== 1 ? 's' : ''} selected
                    </span>
                    <button
                        className="gallery-bulk-download"
                        onClick={handleDownloadSelected}
                    >
                        <i className="fas fa-download"></i> Download Selected
                    </button>
                    <button
                        className="gallery-bulk-clear"
                        onClick={handleClearSelection}
                    >
                        <i className="fas fa-times"></i> Clear
                    </button>
                </div>
            )}

            {/* Album Modal */}
            {showAlbumModal && selectedAlbum && !selectedAlbum.googlePhotosLink && (
                <div className="gallery-modal" onClick={() => setShowAlbumModal(false)}>
                    <div className="gallery-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="gallery-modal-header">
                            <div>
                                <h3 className="gallery-modal-title">{selectedAlbum.title}</h3>
                                <p className="gallery-modal-subtitle">
                                    {selectedAlbum.photoCount} photos • 
                                    Uploaded {formatRelativeTime(selectedAlbum.createdAt)}
                                </p>
                            </div>
                            <button
                                className="gallery-modal-close"
                                onClick={() => setShowAlbumModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="gallery-modal-body">
                            {selectedAlbum.photos.map((photo, index) => (
                                <div key={index} className="gallery-modal-photo">
                                    <LazyLoadImage
                                        src={photo}
                                        alt={`${selectedAlbum.title} - ${index + 1}`}
                                        effect="blur"
                                        placeholderSrc="https://via.placeholder.com/400x300?text=Loading..."
                                        onClick={() => window.open(photo, '_blank')}
                                        className="gallery-modal-lazy-image"
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="gallery-modal-footer">
                            <button
                                className="gallery-btn-primary"
                                onClick={() => handleDownloadAlbum(selectedAlbum)}
                            >
                                <i className="fas fa-download"></i> Download All ({selectedAlbum.photoCount})
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gallery;
